import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Compass, Handshake } from "@phosphor-icons/react";

const SHIP_IMG =
  "https://images.unsplash.com/photo-1670121180530-cfcba4438038?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHNoaXAlMjBwb3J0fGVufDB8fHx8MTc3ODgxNjY0OXww&ixlib=rb-4.1.0&q=85";

const MAP_BG =
  "https://static.prod-images.emergentagent.com/jobs/f7842974-09b3-4b3e-bebd-5a0c602980d0/images/c20eae420d29f9b75246b3a82a6a8a3ecdbeb13f21d9b52d264b2b71e5897254.png";

const pillars = [
  {
    Icon: ShieldCheck,
    title: "Disciplined Execution",
    text:
      "Contracts honoured, documents precise, commitments met on schedule.",
  },
  {
    Icon: Compass,
    title: "Focused Sourcing",
    text:
      "Operating within a defined set of trade corridors with direct supplier relationships.",
  },
  {
    Icon: Handshake,
    title: "Relationship-Led",
    text:
      "We build trade through trust — cultivated patiently across cargos and cycles.",
  },
];

export const About = () => {
  return (
    <section
      id="about"
      className="relative bg-white py-28 lg:py-36 overflow-hidden"
      data-testid="about-section"
    >
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: `url(${MAP_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-vynex-gold" />
              <span className="eyebrow text-vynex-emerald">About</span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
              className="font-display text-4xl lg:text-[56px] leading-[1.05] tracking-tight text-vynex-navy"
              data-testid="about-headline"
            >
              A reliable partner in{" "}
              <span className="italic text-vynex-emerald">
                global commodity trade.
              </span>
            </motion.h2>

            <p className="mt-8 text-slate-600 text-base lg:text-lg leading-relaxed max-w-xl">
              Vynex Global Pte Ltd is a Singapore-incorporated company focused
              on physical commodities trading — particularly metals sourcing
              and cross-border supply coordination.
            </p>
            <p className="mt-5 text-slate-600 text-base lg:text-lg leading-relaxed max-w-xl">
              We approach the business with discipline, transparency, and a
              long-term mindset — building supplier and buyer relationships
              that compound over time, not within a single transaction.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={SHIP_IMG}
                alt="Cargo ship at port"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-vynex-navy/35 to-transparent" />
            </div>
            <div className="absolute -bottom-8 -left-6 lg:-left-12 bg-vynex-navy text-white p-8 max-w-xs shadow-2xl">
              <div className="eyebrow text-vynex-gold mb-3">Headquartered</div>
              <div className="font-display text-2xl leading-tight">
                Singapore — a well-regulated commodities trading jurisdiction.
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-32 grid md:grid-cols-3 gap-px bg-vynex-silver border border-vynex-silver">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-white p-10"
              data-testid={`about-pillar-${i}`}
            >
              <p.Icon size={32} weight="thin" color="#087a55" />
              <h3 className="mt-6 font-display text-2xl text-vynex-navy">
                {p.title}
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
